<script type="ts">
  import FaArrowCircleLeft from "svelte-icons/fa/FaArrowCircleLeft.svelte";
  import FaPlusSquare from "svelte-icons/fa/FaPlusSquare.svelte";
  import FaRegCalendarAlt from "svelte-icons/fa/FaRegCalendarAlt.svelte";
  import FaShareAlt from "svelte-icons/fa/FaShareAlt.svelte";
  import FaArrowsAltH from 'svelte-icons/fa/FaArrowsAltH.svelte';
  import FaRegTimesCircle from "svelte-icons/fa/FaRegTimesCircle.svelte";
  import FaCheckCircle from 'svelte-icons/fa/FaCheckCircle.svelte';
  import html2canvas from "html2canvas";
  import { tooltip } from "svooltip";
  import { visualizationDataStore, showHeaderStore } from "../stores";
  import { fade } from "svelte/transition";
  import { onMount, tick, type ComponentProps } from 'svelte';
  import CustomPeriodSelector from "../../ui-components/custom-period-selector.svelte";
  import Table from "../../ui-components/table.svelte";
  import Button from "../../ui-components/button.svelte";
  import Loader from "../../ui-components/loader.svelte";
  import FilterSelect from "../../ui-components/filter-select.svelte";
  import VisualizationContainer from "../../ui-components/visualization-container.svelte";
  import CustomError from "../../ui-components/custom-error.svelte";
  import IconDropdown from "../../ui-components/icon-dropdown.svelte";
  import DatePicker from "../../ui-components/date-picker.svelte";
  import { monthShortToLongDict } from "../data-source-interface";
  import {
    DataValueType,
    DateFilter,
    ShareTable,
    ShareTableDaily,
  } from "../options";
  import type {
    TableData,
    VisualizationData,
    DataSourceInterface,
  } from "../data-source-interface";
  import type { SelectorSelections, SeriesOptions } from "../types";
  import {
    selectorSelectionsToSeriesOptions,
    keyOf,
    generateUID,
    createQuery,
    sortByDisabledAndSelected,
    sortOptionsAlphabetically,
    updateProps,
    checkDateIncludedInArray,
    showContainerHeader,
    hideContainerHeader,
    handleHeader,
  } from "../utils";
  import { t, locale, findKeyByValue } from "../../translations/i18n";
  import translations from "../../translations/translation";
  import {
    trackStatisticSelected,
    trackLocationSelected,
    trackTypeSelected,
    trackCountryOfResidenceSelected,
  } from "../../utils/analytics";
  
  export let dataSource: DataSourceInterface;

  /** Control when "Add data" & "Back" buttons are shown in the selector */
  export let showSelectorButtons: boolean;

  /** FilterSelect props in this collection are rendered into the selector */
  export let filterSelectProps: ComponentProps<FilterSelect>[] = [];

  /** Reload the filter select props when the filterSelectPropsChanged variable is changed. */
  let filterSelectPropsChanged: boolean = false;

  // If the view is being restored from a previous session, the data is loaded from the storage
  export let restoreView: boolean = false;
  export let storageData: any = [];

  /** Matrix format for TableData */
  type TableDataMatrix = (number | string | null)[][];
  type TableVisualisationData = {
    data: TableDataMatrix;
    dates: Date[];
    metadata: SeriesOptions[];
  };

  /** Default options for the UI */
  const options: any = {
      dataValueType: DataValueType.ABSOLUTE,
      dateFilter: [DateFilter.MONTHS],
    };

  /** Statistics which are shown as percentages in the UI **/
  const percentageStats: { [key: string]: string[] } = {
    "en": [
      "Accommodation utilization",
    ],
    "fi": [
      "Majoituksen käyttöaste",
    ],
    "sv": [
      "Inkvarteringsbeläggning",
    ],
    "nb": [
      "Bruk av overnattingssteder",
    ],
  }

  let tableData: TableVisualisationData;
  $: tableData = {data: [[]], dates: [], metadata: []};

  let dataCollection: TableData = [];

  let visualizationData: VisualizationData;

   /** Unique id for each visualization */
  let uniqueId: string = restoreView ? storageData.id : generateUID();

  /** Collection all metadata from chart*/
  let metadata_array: any;

  /** "Add data" button should be disabled until there is a selection on each field */
  let addDataButtonDisabled: boolean = true;

  /** Initial state for the loaders */
  let showSelectorLoader: boolean = false;

  /** Control loader visibility */
  let showLoader: boolean = false;

  /** Contol buttons visibility (buttons are inactivated until first data is loaded) */
  let buttonsDisabled: boolean = true;

  /** Period selector button disable control in case of daily data */
  let isDaily: boolean = false;

  /** Showing an error message when the data is unavailable. */
  let error_message: boolean = false;

  /** Show additional error info on restoreDataHandler error */
  let restoreDataError: boolean = false;

  /** Selections made on the selector */
  let selectorSelections: SelectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };

  /** Set Table Description */
  let description_element: any;

  /** Set Table Source */
  let source_text: string;

  /** Check wheather to show to custom period selector */
  let customPeriodSelected: boolean = false;
  $: showCustomPeriodSelector = customPeriodSelected;

  /** Variable for restoring the order of columns on data changes by a "Choose Period" */
  let previousColumns: (string | number | null)[];

  /** Variable containing the current language of the page */
  let currentLanguage: any = $locale.split("-")[0]

  /** Variable for initial talbe sorting */
  let sortedAscending: boolean;
  let sortedBy: string;

  /** Variable containing the current focused element index */
  let focusedElementIndex: number = 0;

  /** Variable for controlling visibility of date range picker */
  let showDateRangePicker: boolean = false;
  let dateRangePickerDisabled: boolean = false;
  let dateRangePickerFromDate: string | undefined;
  let dateRangePickerToDate: string | undefined;

  $: dateRangePickerDisabled = !options.dateFilter.includes(DateFilter.MONTHS);

  /** Variable for controlling DatePicker component visibility */
  let showDatePicker: boolean = false;
  let datePickerValue: string | Date | undefined;
  let datePickerDisplay: string | undefined;

  /** Cache for original tableData not to make additional API calls */
  let originalTableData: TableDataMatrix = [];

  /** Functionality for hiding headers */
  let showHeader: any;
  showHeaderStore.subscribe(value => {
    showHeader = value;
  });
  $: handleHeader(showHeader, uniqueId);

  /**
     * Function to call getTableData for getting data from Visitory API. 
     * This function is used for showing table data.
  */
  const addTableHandler = async (): Promise<void> => {
    try {
      await tick();
      hideContainerHeader(uniqueId);

      // Variable for storing the sources of the data
      let sources: Array<string> = [];
      showLoader = true;

      // If any daily data added to the table, then the period selector will be disabled.
      if (selectorSelections.is_daily){
        isDaily = true;
      }

      const tableSeriesOptions = selectorSelectionsToSeriesOptions(selectorSelections);

      // Create query from series options
      await Promise.all(
        tableSeriesOptions.map(async (seriesOption) => {
          const query = createQuery(options, seriesOption);
          const data: TableData = await dataSource.getTableData(query);
          const dataId: number = tableData.metadata.length;
          data.forEach(element => {
            element.id = dataId;
            sources.includes(element.source) ? null : sources.push(element.source);
          });

          // If the data is already in the table, don't add it again
          if (compareSeriesOptions(tableData.metadata, seriesOption)) {
            return;
          }
          if (data.length == 0) {
            return;
          }

          dataCollection = dataCollection.concat(data);
          tableData.metadata.push(seriesOption);
        })
      );
      tableData.data = DataToTableData(dataCollection, tableData.metadata);
      tableData.dates = dataCollection
        .map((d) => new Date(d.dateFrom))
        .filter((date, index, self) => self.findIndex((d) => d.getTime() === date.getTime()) === index) // Remove duplicates (Not sure that applies to all cases)
        .sort((a: Date, b: Date) => b.getTime() - a.getTime());

      calculateDatesForRangePicker(tableData.dates);

      // Initial sorting of the table data (can't be done in the Table component)
      const columnName = String(tableData.data[0][0]);
      sortedAscending = false;
      sortedBy = columnName;
      tableData.data = sortRowsByColumn(tableData.data, columnName, sortedAscending);

      let description_text = ""
      // Remove nulls from the sources array
      sources = sources.filter(n => n)
      if (source_text != undefined && source_text != null && source_text != "") {
        source_text = `${source_text}, ${sources.join(", ")}`;
      }
      else {
        source_text = `${sources.join(", ")}`;
      }

      // Add description text as innerHTML to description_element with <br> tags
      // Otherwise everything will be shown as one line
      tableData.metadata.forEach((statistic, index) => {
        let optionsDescription = '';
        Object.entries(statistic.verbose_options).forEach(([key, value]) => {
          optionsDescription += `${key}: ${value[currentLanguage]}, `;
        });

        optionsDescription = optionsDescription.slice(0, -2); // Remove the trailing comma and space

        description_text += `${index + 1}. ${statistic.statVerboseName[currentLanguage]} (${optionsDescription})<br>`;
      });

      // Add description text as innerHTML to description_element with <br> tags
      description_element.innerHTML = description_text;

      // Handle header depending on showHeader state
      handleHeader(showHeader, uniqueId);

      // If there is no data, show error message (ideally shouldn't happen)
      if (tableData.data[0] == undefined) {
        error_message = true;
      } else {
        storeVisualizationData(selectorSelections);
      } 

      originalTableData = tableData.data;
      showLoader = false;
      buttonsDisabled = false;
      resetSelector();

    } catch (error) {
      error_message = true;
      console.error("Error in addTableHandler: ", error);
    }
  };


  /** 
   * Function to compare two series options and determine if they are equal
   * @returns true if the series options are equal, false otherwise
   */
  const compareSeriesOptions = (metadata: SeriesOptions[], seriesOption: SeriesOptions): boolean => {
    const isDuplicate = metadata.some(existingMetadata => {
        if (existingMetadata.statName !== seriesOption.statName) {
          return false; // Different statName - not a duplicate
        }
        return Object.keys(existingMetadata.options).every(key => {
          return (
            key in seriesOption.options && existingMetadata.options[key] === seriesOption.options[key]
          );
        });
      });
    return isDuplicate;
  };


  /**
   * Handler for ui components to restore saved data to the table
   */
  const restoreDataHandler = async (visualization: any): Promise<void> => {
    try {
      const containerHeader = document.getElementById(`header-id-${uniqueId}`);
      if (containerHeader) {
        containerHeader.style.display = "none";
      }
      showLoader = true;
      let sources: Array<string> = [];

      for (let i = 0; i < visualization.metadata.length; i++){

        const tableSeriesOptions = selectorSelectionsToSeriesOptions(visualization.metadata[i]);
        selectorSelections.is_daily = visualization.metadata[i].is_daily;

        // If any daily data added to the table, then the period selector will be disabled.
        if (selectorSelections.is_daily){
          isDaily = true;
        }

        // Create query from series options
        await Promise.all(
          tableSeriesOptions.map(async (seriesOption) => {
            const query = createQuery(options, seriesOption);
            const data: TableData = await dataSource.getTableData(query);
            const dataId: number = tableData.metadata.length;
            data.forEach(element => {
              element.id = dataId;
              sources.includes(element.source) ? null : sources.push(element.source);
            });
            
            // If the data is already in the table, don't add it again
            if (compareSeriesOptions(tableData.metadata, seriesOption)) {
              return;
            }
            if (data.length == 0) {
              return;
            }

            dataCollection = dataCollection.concat(data);
            tableData.metadata.push(seriesOption);
          })
        );
        tableData.data = DataToTableData(dataCollection , tableData.metadata);
        tableData.dates = dataCollection
          .map((d) => new Date(d.dateFrom))
          .filter((date, index, self) => self.findIndex((d) => d.getTime() === date.getTime()) === index) // Remove duplicates (Not sure that applies to all cases)
          .sort((a: Date, b: Date) => b.getTime() - a.getTime());

        // Initial sorting of the table data (can't be done in the Table component)
        const columnName = String(tableData.data[0][0]);
        sortedAscending = false;
        sortedBy = columnName;
        tableData.data = sortRowsByColumn(tableData.data, columnName, sortedAscending);
      }
      calculateDatesForRangePicker(tableData.dates);

      let description_text = "";
      // Remove nulls from the sources array
      sources = sources.filter(n => n)
      source_text = `${sources.join(", ")}`;

      // Add description text as innerHTML to description_element with <br> tags
      // Otherwise everything will be shown as one line
      tableData.metadata.forEach((statistic, index) => {
        let optionsDescription = '';
        Object.entries(statistic.verbose_options).forEach(([key, value]) => {
          optionsDescription += `${key}: ${value[currentLanguage]}, `;
        });

        optionsDescription = optionsDescription.slice(0, -2); // Remove the trailing comma and space

        description_text += `${index + 1}. ${statistic.statVerboseName[currentLanguage]} (${optionsDescription})<br>`;
      });
      description_element.innerHTML = description_text;

      // If there is no data, show error message (ideally shouldn't happen)
      if (tableData.data[0] == undefined) {
        restoreDataError = true;
        error_message = true;
      }

      originalTableData = tableData.data;

      // Show the visualization header
      handleHeader(showHeader, uniqueId);
      
      showLoader = false;
      buttonsDisabled = false;
      resetSelector();
    } catch (error) {
      restoreDataError = true;
      error_message = true;
      console.error("Error in restoreDataHandler: ", error);
    }
  }

  /**
   * Storing visualization data to be restored later
   */
  const storeVisualizationData = (selectorSelections: SelectorSelections): void => {
    let storedVisualizationData: VisualizationData[] = [];
    let containerSize: string = 'h-large-v-small';
    visualizationDataStore.subscribe(
      (value) => {
        storedVisualizationData = value;
      }
    )

    const existingIndex = storedVisualizationData.findIndex((item) => item.id === uniqueId);
    if (existingIndex !== -1) {
      containerSize = storedVisualizationData[existingIndex].visualizationSize ?? '';
    }

    metadata_array.push(selectorSelections);
    visualizationData = {
      id: uniqueId,
      type: "Table",
      metadata: metadata_array,
      period: options.dateFilter,
      visualizationSize: containerSize,
    };
    visualizationDataStore.updateCollection(visualizationData);
  };

  /**
   * Update Period(Date Filter) of visualization data to Store on browser database.
   */
  const updatePeriod = (period: any): void => {
    visualizationData.period = period;
    visualizationDataStore.updateCollection(visualizationData);
  };


  /**
   * Handler for exporting the table as PNG or CSV
   * @param type - Type of export (PNG or CSV)
   */
  const exportHandler = (type: any): void => {
    if (buttonsDisabled) return;
    switch (type) {
      case ShareTable.PNG:
        exportPNG();
        break;
      case ShareTable.CSV:
        exportCSV();
        break;
    }
  };

  /**
   * Function to export table as CSV file.
  */
  const exportCSV = (): void => {
    const separator = ";";
    const filename = "VisitoryData.csv";

    function percentageToDecimal(element: string, delimiter: string = '.'): string {
      return element.endsWith('%') ?
        (parseFloat(element.replace(" %", '')) / 100).toString().replace('.', delimiter) :
        element;
    }

    let lineArray: any = [];
    tableData.data.forEach(function (infoArray, index) {
      infoArray.forEach(function(element, index) {
        if (element) {
          let cleanedElement = element.toString().trim();
          switch (currentLanguage) {
            case "en":
              cleanedElement = cleanedElement.replaceAll(',','');
              cleanedElement = percentageToDecimal(cleanedElement);
              break;
            case "fi":
              cleanedElement = percentageToDecimal(cleanedElement, ',');
              cleanedElement = cleanedElement.replaceAll(/(\d)\s(?=\d)/g, '$1');
              break;
            case "sv":
              cleanedElement = percentageToDecimal(cleanedElement, ',');
              cleanedElement = cleanedElement.replaceAll(/(\d)\s(?=\d)/g, '$1');
              break;
            case "nb":
              cleanedElement = percentageToDecimal(cleanedElement, ',');
              cleanedElement = cleanedElement.replaceAll(/(\d)\s(?=\d)/g, '$1');
              break;
          }
          infoArray[index] = cleanedElement;
        }
      });
      let line = infoArray.join(separator);
      lineArray.push(line);
    });
    
    let csvContent = lineArray.join("\n");
    const bom = "\uFEFF";

    // Create a Blob object with the CSV content
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a temporary download link
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      // Download the file
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportPNG = (): void => {
    const filename = "VisitoryData.png";

    // Find the table element
    const table = document.getElementById(`table-content-${uniqueId}`);

    if (!table) {
      console.error("Table element not found");
      return;
    }

    // Save the original styles
    const originalStyles: any = table.getAttribute("style");

    // Set the table styles for printing
    table.style.position = "fixed";
    table.style.display = "flex";
    table.style.flexDirection = "column";
    table.style.alignItems = "center";

    // Create an image element for the logo
    // BUG: The logo image is not shown in the exported PNG
    /*
    const logoImage = document.createElement("img");
    logoImage.src = visitoryPNGLogoUrl;
    logoImage.style.zIndex = "999999";
    logoImage.style.height = "50px";
    */

    // Create a paragraph element for mentioning the source for the exported files
    let print_source_text = `Visitory.io (${$t("Sources")}: ${source_text})`;
    let table_source_content = table.querySelector("#table-source-text");
    if (table_source_content != null){
      table_source_content.innerHTML = print_source_text;
    }

    // Scroll the table to the top-left corner
    table.scrollIntoView();

    // Capture the table element as a canvas
    html2canvas(table).then((canvas) => {
      // Convert the canvas to a PNG image data URL
      const dataUrl = canvas.toDataURL("image/png");

      // Create a temporary download link
      const link = document.createElement("a");
      if (link.download !== undefined) {
        link.setAttribute("href", dataUrl);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        // Download the file
        link.click();
        document.body.removeChild(link);

        // Restore the original styles
        table.setAttribute("style", originalStyles);

      }
    });
    if (table_source_content != null) {
      table_source_content.innerHTML = `${$t("Sources")}: ${source_text}`;
    }
  };

  /**
   * "Back button" handler
   *
   * Clicking back should take the user to the previous step in the selector.
   * For example, clicking back from the fields should show the statistics again.
   *
   * TODO: Implement more steps in the selector
   */
   const backHandler = (): void => {
    resetSelector();
  };

  /**
   * Reset selector to the initial state
   */
   const resetSelector = (): void => {
    addDataButtonDisabled = true;
    selectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };
    addStatistics();
  };

  /** Add statistics into the selector */
  const addStatistics = async (): Promise<void> => {
    // Hide the "Add data" & "Back" buttons because clicking a stat takes the
    // user directly to the next "level"
    showSelectorButtons = false;
    showSelectorLoader = true;

    const statistics = await dataSource.getStatistics();
    // First, sort options alphabetically
    const sortedAlphabetically = sortOptionsAlphabetically(statistics.map((s) => ({
      model: s.model,
      name: s.name,
      title: s.description,
      keywords: s.keywords,
      disabled: s.disabled ? s.disabled : ((isDaily && !s.is_daily && metadata_array.length > 0) || (!isDaily && s.is_daily && metadata_array.length > 0)),
      is_daily: s.is_daily,
    })), true);

    // Then, sort the options by the disabled property
    sortedAlphabetically.sort((a: any, b: any) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));

    filterSelectProps = [
      {
        title: $t("Statistics"),
        options: sortedAlphabetically,
        visualizationId: `container-id-${uniqueId}`,
        selectCallback: async (_title, selected, _selectClicked) => {
          const statName = selected?.[0]?.model || "";
          const statVerboseName = selected?.[0]?.name || "";
          const is_daily = selected?.[0]?.is_daily || false;
          selectorSelections.statName = statName;
          selectorSelections.statVerboseName = statVerboseName;
          selectorSelections.is_daily = is_daily;
          trackStatisticSelected(statVerboseName[$locale.split('-')[0]] || statName, true);
          await addFields(statName);
        },
      },
    ];

    showSelectorLoader = false;
    
  };

  /**
   * Add fields into the selector based on the selected statistic
   *
   * @param statName - Name of the statistic
   */
  const addFields = async (statName: string): Promise<void> => {

    showSelectorLoader = true;
    let updatedFields: any = []

    const fields = await dataSource.getFields(statName);
    filterSelectProps = fields.map((field) => {
      return {
        name: field.name,
        field: field.field,
        title: field.name[currentLanguage],
        visualizationId: `container-id-${uniqueId}`,
        statModel: selectorSelections.statName,
        typeField: field.field,
        options: sortOptionsAlphabetically(field.options.map((option) => ({
          name: option.name,
          disabled: option.disabled,
          parent_country: option.parent_country,
        })), true),
        multiselect: true,
        selectCallback: async (title, selected, _selectClicked) => {
          const values = selected?.map((s) => s.name["en"]) || [];
          const fieldVerboseName = selected?.map((s) => s.name) || [];
          
          // Track filter selections
          selected.forEach((selection) => {
            const selectionName = selection.name[$locale.split('-')[0]] || selection.name["en"];
            if (title.toLowerCase().includes('location')) {
              trackLocationSelected(selectionName, true);
            } else if (title.toLowerCase().includes('type')) {
              trackTypeSelected(selectionName, true);
            } else if (title.toLowerCase().includes('country')) {
              trackCountryOfResidenceSelected(selectionName, true);
            }
          });
          
          selectorSelections.selections[field.field] = {
            values: fieldVerboseName as Object[],
            values_db: values as string[],
            verbose_key_name: field.name,
          };

          // Preventing user to click unavailable combinations of fields
          showSelectorLoader = true;

          // Call the function to get the disabled fields based on selection.
          updatedFields = await dataSource.updateDisabledFields(
            selectorSelections.selections,
            statName
          );

          showSelectorLoader = false;

          // Update the props of the fields
          filterSelectPropsChanged = !filterSelectPropsChanged;
          updateProps(updatedFields, filterSelectProps);

          // Sort the options alphabetically
          sortOptionsAlphabetically(filterSelectProps);

          // Sort the fields by disabled and selected values
          sortByDisabledAndSelected(filterSelectProps);

          // "Add data" button enabled if all fields have at least one selection
          addDataButtonDisabled =
            Object.keys(selectorSelections.selections).filter(
              (key) => selectorSelections.selections[key].values_db.length > 0
            ).length != fields.length;
        },
      };
    });

    showSelectorButtons = true;
    showSelectorLoader = false;
  };

  /**
   * Convert data from TableData to TableDataMatrix
   *
   * @param data Data in TableData format
   * @returns Data in TableDataMatrix format
   */
   const DataToTableData = (data: TableData, metadata:any): TableDataMatrix => {
    let tableData: TableDataMatrix = [];

    // Get the first location as a reference point
    const referenceLocation = metadata[0].options.location;

    // Assume all locations are the same initially
    let allLocationsSame = true;

    // Loop through the array and check if any location is different
    for (const item of metadata) {
      if (item.options.location !== referenceLocation) {
        allLocationsSame = false;
        break;
      }
    }
    const rows = [...new Set(data.map((d) => d.row))];
    let columns: { column_value: string; id: number }[] = [];

    columns = data.reduce((accumulator: { column_value: string; id: number }[], d) => {
      let columnValue;
      if (metadata.length == 1 || allLocationsSame ){
        // If only one metadata item or all locations are the same, don't show the location in the column title
        columnValue = (d.id as number) + 1 + ". " + d.statName;
      } else {
        // If multiple metadata items and locations are different, show the location in the column title
        columnValue = (d.id as number) + 1 + ". " + d.statName + " (" + d.column + ")";
      }
      const existingItem = accumulator.find((item) => item.column_value === columnValue && item.id === d.id);
      if (!existingItem && d.id !== undefined) {
        accumulator.push({ column_value: columnValue, id: d.id });
      }
      return accumulator;
    }, []);

    tableData = [[$t("Date"), ...columns.map((c) => c.column_value)]];
    rows.forEach((row) => {
      let rowArray: (number | string | null)[] = [];
      rowArray.push(row);
      columns.forEach((column) => {
        let value;
        if (metadata.length == 1 || allLocationsSame ){
          value = data.find((d) => d.row === row && (d.id as number) + 1 + ". " + d.statName  === column.column_value && d.id === column.id );
        }
        else {
          value = data.find((d) => d.row === row && (d.id as number) + 1 + ". " + d.statName + " (" + d.column + ")" === column.column_value && d.id === column.id );
        }
        // Change some stat values to be shown as percentages
        if (value?.value && percentageStats[currentLanguage].includes(value?.statName)) {
          value.value = value.value.toString().endsWith('%') ? value.value :
            Math.round(parseFloat(value.value.toString().replace(',', '.')) * 100).toString().concat(" %");
        }
        rowArray.push(value ? (value.value !== null && !String(value.value).includes("null") ? value.value : "") : null);
      });
      tableData.push(rowArray);
    });
    // Remove rows with only null values
    tableData = tableData.filter((row) => row.slice(1).some((value) => value !== null));

    // Establish the differences between the metadata
    let differences: Array<string> = [];
    metadata.forEach((item: TableData[0]) => {
      // Check if all stats in all items are the same
      if (metadata.length > 1 && metadata[0].statName !== item.statName && !differences.includes("statName")) {
        differences.push("statName");
      }
      // Location is the second priority so check it before other options
      if (metadata[0].verbose_options["Location"] !== undefined && item.verbose_options["Location"] !== undefined) {
        if (metadata.length > 1 && metadata[0].verbose_options["Location"][currentLanguage] !== item.verbose_options["Location"][currentLanguage] && !differences.includes("Location")) {
          differences.push("Location");
        }
      }
      // Loop through all other verbose_options and which of these are different
      Object.entries(item.verbose_options).forEach((element) => {
        if (differences.length < 2) {
          // Compare each element of verbose_options to the first element of the first item
          if (metadata[0].verbose_options[element[0]] != undefined && item.verbose_options[element[0]] != undefined) { // Would be undefined if the first item doesn't have the option
            if (metadata.length > 1 && metadata[0].verbose_options[element[0]][currentLanguage] !== element[1][currentLanguage] && !differences.includes(element[0])) { // Bed nights + Bed night forecasts -> “foreign” in place of residence results in a bug
              differences.push(element[0]);
            }
          }
        }
        // Remove options from differences if they don't exist in the verbose_options of some item
        differences = differences.filter(e => e != "statName" ? Object.keys(item.verbose_options).includes(e) : true);
      });
    });

    // Form the column titles based on the differences
    let columnTitles: string[] = [];
    metadata.forEach((item: any, index: number) => {
      let columnTitle = ""
      if (differences.includes("statName") || differences.length == 0) {
        columnTitle = `${(index as number) + 1}. ${item.statVerboseName[currentLanguage]}`;
        if (differences.length > 1) {
          columnTitle += " (";
        }
      } else {
        columnTitle = `${(index as number) + 1}. `;
      }
      differences.forEach((element) => {
        if (element != "statName") {
          columnTitle += item.verbose_options[element][currentLanguage] + ", ";
        } 
      });
      if (differences.includes("statName") && differences.length > 1) {
        columnTitle = columnTitle.slice(0, -2); // Remove the trailing comma and space
        columnTitle += ")";
      } else if (!differences.includes("statName") && differences.length > 0) {
        columnTitle = columnTitle.slice(0, -2); // Remove the trailing comma and space
      }
      columnTitles.push(columnTitle);
    });

    // Add date to the beginning of the column titles since first column is always date
    columnTitles.unshift($t("Date"));
    
    // Add the column titles to the beginning of the table data
    tableData[0] = columnTitles;

    if ((previousColumns) && (previousColumns.length == tableData[0].length)) {
      // Reorder columns by matching the previous columns
      tableData = reorderColumns(tableData, previousColumns);
    }

    return tableData;
  };

  /**
   * Update data in the table based on the selected options
   */
   const updateDataHandler = async (): Promise<void> => {
    showLoader = true;

    if (options.dateFilter.includes("-")){
      let [startMonth, endMonth] = options.dateFilter.split(" - ");
      startMonth = monthShortToLongDict[startMonth];
      endMonth = monthShortToLongDict[endMonth];
      options.dateFilter = [startMonth, endMonth];
    }

    // Convert date from string to DateFilter list when the filter is not custom.
    if (typeof(options.dateFilter) !== "object" && options.dateFilter !== undefined){
      const input: string = options.dateFilter;
      let dateFilter: DateFilter = input as DateFilter;
      options.dateFilter = [dateFilter];
    }

    // Set all combinations of the data
    dataCollection = [];
    // Create a copy of the metadata
    let seriesOptions = tableData.metadata;
    // Clear the metadata to assign the new ids.
    tableData.metadata = [];
    // Create query from series options
    await Promise.all(
      seriesOptions.map(async (seriesOption) => {
        const query = createQuery(options, seriesOption);
        const data: TableData = await dataSource.getTableData(query);
        const dataId: number = tableData.metadata.length;
        data.forEach(element => {
          element.id = dataId;
        });

        // Add old metadata to the new metadata
        tableData.metadata.push(seriesOption);
        dataCollection = dataCollection.concat(data);
      })
    );

    // Store the previous columns to restore the order of columns on data changes
    previousColumns = tableData.data[0]

    // Set dataCollection to the table data 
    tableData.data = DataToTableData(dataCollection, tableData.metadata);
    updatePeriod(options.dateFilter);
    showLoader = false;
  };

  /**
   * Sort the rows by the column
   * @param tableData 
   * @param column 
   * @param ascending 
   * @returns 
   */
   const sortRowsByColumn = (
    tableData: TableDataMatrix,
    column: string,
    ascending: boolean = true
  ) => {
    const columnIndex = tableData[0].indexOf(column);
    
    const sortedTableData = tableData.slice(1).sort((a, b) => {
      const valueA = parseValue(a[columnIndex]);
      const valueB = parseValue(b[columnIndex]);

      if (valueA === valueB) return 0;

      // If the value is numeric, it should be sorted as number1 Tammi, 2023 	
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return ascending ? valueA - valueB : valueB - valueA;
      }

      // If the value is null, it should be sorted to the bottom
      if (valueA === null) return 1;
      
      // If the value is stirng, it should change to date and then sort
      const dateA = parseMonthYear(valueA);
      const dateB = parseMonthYear(valueB);

      // If the value is date, it should be sorted as date
      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return 1;
      if (dateB === null) return -1;

      if (dateA < dateB) return ascending ? -1 : 1;
      if (dateA > dateB) return ascending ? 1 : -1;
      return 0;
    });

    return [tableData[0], ...sortedTableData];
  };

  /** 
   * Function to remove the prefix numbers (e.g., "1. ") from the column names 
   * @param name
  */
  function removePrefix(name: string | number | null) {
    return name ? (name.toString()).replace(/^\d+\.\s*/, "") : "";
  }

  /**
   * Reorder columns by matching inputed column order
   * @param matrix
   * @param desiredOrder
   */
  function reorderColumns(dataMatrix: TableDataMatrix, desiredColOrder: (string | number | null)[]) {
    // Create a map of original headers without prefixes (order numbers)
    const originalMap = desiredColOrder.map(removePrefix);

    // Get the index mapping for the updated headers based on the desired order
    const indexMap = originalMap.map(originalHeader => 
      dataMatrix[0].findIndex(updatedHeader => removePrefix(updatedHeader) === originalHeader)
    );

    let reorderedData = dataMatrix.map(row => indexMap.map(i => row[i]));

    // Replace column headers with the original column to maintain the numbering order
    reorderedData[0] = desiredColOrder;

    return reorderedData;
  }
  
  /**
   * Parse the month year string to date 
  */
  function parseValue(value: any) {
    if (isNumeric(value)) {
      // Remove commas and parse as a float
      return parseFloat(value.replace(/,/g, ''));
    }
    return value;
  }

  /**
   * Parse the month year string to date 
  */
  function parseMonthYear(value: any) {
    if (value === null) return null;
    if (isNumeric(value)) return null;
    const monthList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let standardizedDate;

    const monthYearString = String(value).replace(",", "");

    // Check if the date is daily date, pass the day with month and year, else pass the month and year
    if (isDaily){
      const [day , month, year] = monthYearString.split(' ').slice(-3);
      standardizedDate = new Date(Number(year), monthList.indexOf(findKeyByValue(translations[$locale], month)), Number(day));
    } else {
      const [month, year] = monthYearString.split(' ').slice(-2);
      
      if (!month || !year) return null;
      standardizedDate = new Date(Number(year), monthList.indexOf(findKeyByValue(translations[$locale], month)));
    }
    if (isNaN(standardizedDate.getTime())) return null;

    return standardizedDate;
  };

  /**
   * Check if the input is a valid daily date string.
   * @param input 
   * @returns 
   */
  function isValidDailyDate(value: any) {
    const datePattern = /^(\d{1,2})\s+([A-Za-z]+),\s+(\d{4})$/;
    return datePattern.test(value);
  }

  /**
   * Check if the input is a number
   * @param value 
   * @returns 
   */
  function isNumeric(value: any) {

    if (value === null) {
      return false;
    }

    const sanitizedValue = String(value).replace(/,/g, '');
    
    // If the value is a DAILY date, it should be treated as a number
    if (isValidDailyDate(value)) {
      return !isNaN(Number(sanitizedValue));
    } else {
      return !isNaN(parseFloat(sanitizedValue));
    }
  };

  /**
   * Handler for period selector
   */
  const customPeriodSelectorCloseHandler = (event : any) => {
    const { selectedFrom, selectedTo } = event.detail;
    if (selectedFrom == ''){
      customPeriodSelected = false;
    } else {
      options.dateFilter = [selectedFrom, selectedTo];
      updateDataHandler();
      customPeriodSelected = false;
    }
  };

  /**
   * Calculates the dates for the date range picker
   * 
   * @param dates - Array of dates 
   */
  const calculateDatesForRangePicker = (dates: Date[]) => {
    if (dateRangePickerFromDate == undefined) {
      let fromDate = dates[dates.length - 1]
      if (isDaily){
        dateRangePickerFromDate = `${fromDate.getDate()}/${fromDate.getMonth() + 1}/${fromDate.getFullYear()}`;
      } else {
        dateRangePickerFromDate = `${fromDate.getMonth() + 1}/${fromDate.getFullYear()}`;
      }
    }
    if (dateRangePickerToDate == undefined) {
      let toDate = dates[0]
      if (isDaily) {
        dateRangePickerToDate = `${toDate.getDate()}/${toDate.getMonth() + 1}/${toDate.getFullYear()}`;
      } else {
        dateRangePickerToDate = `${toDate.getMonth() + 1}/${toDate.getFullYear()}`;
      }
    }
  }

  /**
   * Handler for closing the date range picker
   */
  const closeDateRangePicker = () => {
    showDateRangePicker = false;
  };

  /**
   * Handler for opening the date range picker
   * 
   * @param clickedDateDisplay - The date display that was clicked
   */
  const openDatePicker = (clickedDateDisplay: string) => {
    let dateYear: number;
    let dateMonth: number;
    let dateDay: number;
    let date: Date;

    if (clickedDateDisplay == "from" && dateRangePickerFromDate != undefined) {
      datePickerDisplay = "from";
      if (isDaily) {
        dateYear = parseInt(dateRangePickerFromDate?.split("/")[2]);
        dateMonth = parseInt(dateRangePickerFromDate?.split("/")[1]);
        dateDay = parseInt(dateRangePickerFromDate?.split("/")[0]);
      } else {
        dateYear = parseInt(dateRangePickerFromDate?.split("/")[1]);
        dateMonth = parseInt(dateRangePickerFromDate?.split("/")[0]);
        dateDay = 1;
      }
      date = new Date(dateYear, dateMonth - 1, dateDay);
      datePickerValue = date;
    } else if (clickedDateDisplay == "to" && dateRangePickerToDate != undefined) {
      datePickerDisplay = "to";
      if (isDaily) {
        dateYear = parseInt(dateRangePickerToDate?.split("/")[2]);
        dateMonth = parseInt(dateRangePickerToDate?.split("/")[1]);
        dateDay = parseInt(dateRangePickerToDate?.split("/")[0]);
      } else {
        dateYear = parseInt(dateRangePickerToDate?.split("/")[1]);
        dateMonth = parseInt(dateRangePickerToDate?.split("/")[0]);
        dateDay = 1;
      }
      date = new Date(dateYear, dateMonth - 1, dateDay);
      datePickerValue = date;
    }

    showDatePicker = true;
  };

  /**
   * Handler for selecting a date in the date picker
   */
  const handleDatePickerSelection = (event: any) => {
    const selectedDate = event.detail;
    let month = selectedDate.getMonth() + 1;
    let day = selectedDate.getDate();
    let year = selectedDate.getFullYear();

    let dateDisplayValue = (isDaily ? `${day}/` : "") + `${month}/${year}`;

    if (datePickerDisplay == "from") {
      dateRangePickerFromDate = dateDisplayValue;
    } else if (datePickerDisplay == "to") {
      dateRangePickerToDate = dateDisplayValue;
    }

    showDatePicker = false;
  };

  /**
   * Handler for applying the date range
   * 
   * Filters the table data based on the selected date range
   */
  const handleDateRangeApply = () => {
    let fromDateList: any = dateRangePickerFromDate?.split("/");
    let toDateList: any = dateRangePickerToDate?.split("/");
    let fromDate: Date;
    let toDate: Date;

    // Sort the table data by date to maintain the order of the data
    tableData.data = sortRowsByColumn(tableData.data, String(tableData.data[0][0]), sortedAscending);

    if (isDaily) {
      fromDate = new Date (parseInt(fromDateList[2]), parseInt(fromDateList[1]) - 1, parseInt(fromDateList[0]));
      toDate = new Date (parseInt(toDateList[2]), parseInt(toDateList[1]) - 1, parseInt(toDateList[0]));
    } else {
      fromDate = new Date (parseInt(fromDateList[1]), parseInt(fromDateList[0]) - 1, 1);
      toDate = new Date (parseInt(toDateList[1]), parseInt(toDateList[0]) - 1, 1);
    }

    let fromDateIndex: any = checkDateIncludedInArray(tableData.dates, fromDate, true); // Should be greater than toDateIndex
    let toDateIndex: any = checkDateIncludedInArray(tableData.dates, toDate, true);

    // If user selects dates in reverse order, swap the indexes
    if ( fromDateIndex < toDateIndex) {
      // Swap the indexes if the toDateIndex is greater than fromDateIndex
      [fromDateIndex, toDateIndex] = [toDateIndex, fromDateIndex];
      [dateRangePickerFromDate, dateRangePickerToDate] = [dateRangePickerToDate, dateRangePickerFromDate];
    }

    // Get first row of tableData.data which is the column names
    let columnNames = tableData.data[0];
    tableData.data = [columnNames, ...originalTableData.slice(toDateIndex + 1, fromDateIndex + 2)]
    
    showDateRangePicker = false;
  }

  onMount(async() => {
    if (restoreView){
      // Set default options
      metadata_array = storageData.metadata;
      options.dateFilter = storageData.period;
      // Set Visualization options
      visualizationData = {
        id: storageData.id,
        type: "Table",
        metadata: metadata_array,
        period: storageData.period,
        visualizationSize: storageData.visualizationSize,
      };
      // Call the function to restore the view.
      await restoreDataHandler(storageData);
    } else {
      metadata_array= [];
      await addStatistics();
    }
  });

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = (event: KeyboardEvent): void => {

  let visualisationContainer: HTMLElement = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
  let selectorButtons: NodeListOf<Element>  = visualisationContainer.querySelectorAll("#table-selector-buttons button") as NodeListOf<Element>;
  focusedElementIndex = Array.from(selectorButtons).findIndex((element: Element) => 
    element === document.activeElement || element.contains(document.activeElement)
    );
    if (focusedElementIndex === -1) {
      focusedElementIndex = 0;
  }

  switch (event.key) {
    case "Enter":
      // Set the focus to the parent visualization container
      if (document.activeElement?.id.includes("AddData") || document.activeElement?.id.includes("back")){
        document.getElementById("container-id-" + uniqueId)?.focus();
      }
      break;

    case "ArrowDown":
      // Focus the next element in the list
      if (focusedElementIndex > 0) {
        (selectorButtons[focusedElementIndex - 1] as HTMLElement).focus();
      }
      else {
        // If the first element is focused, focus the last element
        (selectorButtons[selectorButtons.length - 1] as HTMLElement).focus();
      }
      break;

    case "ArrowUp":
      // Focus the previous element in the list
      if (focusedElementIndex < selectorButtons.length - 1) {
        (selectorButtons[focusedElementIndex + 1] as HTMLElement).focus();
      }
      else {
        // If the last element is focused, focus the first element
        (selectorButtons[0] as HTMLElement).focus();
      }
      break;

    case "ArrowLeft":
      // Focus to the filter select component
      let parentVisualizationContainer = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
      let filterPropsContainerList: NodeListOf<Element>  = parentVisualizationContainer.querySelectorAll('[id^="filterPropsContainer"]');
      // Set focus to the last filter select component
      (filterPropsContainerList[filterPropsContainerList.length - 1] as HTMLElement).focus();
      break;

    default:
      // Allow default behavior for other keys
      return;
  }

  // Prevent default behavior only for relevant keys
  event.preventDefault();
  };

</script>

{#if !error_message}
<VisualizationContainer 
  {showLoader} 
  selectorDisabled={buttonsDisabled} 
  visualizationId={uniqueId}
  visualizationData={storageData}>
  <svelte:fragment slot="buttons">
    <button class="date-selector-button {dateRangePickerDisabled ? 'disabled' : ''}" on:click={() => {showDateRangePicker = !showDateRangePicker}} disabled={dateRangePickerDisabled}
      use:tooltip={{
        content: $t("Select date range"),
        placement: 'top',
        delay: [250, 0],
        offset: 15,
        html: true,
        target: 'div.icon',
      }}>
      <div class="date-range-picker-icon">
        <div class="calendar-icon">
          <FaRegCalendarAlt />
        </div>
        <div class="arrow-icon">
          <FaArrowsAltH />
        </div>
      </div>
    </button>
    {#if !isDaily}
      <IconDropdown
        options={ShareTable}
        handler={exportHandler}
        title={$t("Share")}
        disabled={buttonsDisabled}
      >
        <div class="icon" slot="icon"><FaShareAlt /></div>
      </IconDropdown>
    {:else}
      <IconDropdown
        options={ShareTableDaily}
        handler={exportHandler}
        title={$t("Share")}
        disabled={buttonsDisabled}
      >
        <div class="icon" slot="icon"><FaShareAlt /></div>
      </IconDropdown>
    {/if}
    {#if showCustomPeriodSelector == true}
      <CustomPeriodSelector on:close={customPeriodSelectorCloseHandler}/>
    {/if}
    {#if !isDaily}
      <IconDropdown
        state={options}
        key={keyOf(options.dateFilter, options)}
        options={DateFilter}
        handler={updateDataHandler}
        title={$t("Choose period")}
        bind:customPeriod={customPeriodSelected}
        disabled={buttonsDisabled}
        ><div class="icon" slot="icon"><FaRegCalendarAlt /></div>
      </IconDropdown>
    {:else}
      <IconDropdown
        state={options}
        key={keyOf(options.dateFilter, options)}
        options={DateFilter}
        handler={updateDataHandler}
        title={$t("Choose period")}
        bind:customPeriod={customPeriodSelected}
        disabled={true}
        ><div class="icon" slot="icon"><FaRegCalendarAlt /></div>
      </IconDropdown>
    {/if}
  </svelte:fragment>

  <div class="selector" slot="selector">
    <Loader visible = {showSelectorLoader} />

    <div class="filters">
      {#if filterSelectPropsChanged == true}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {:else if filterSelectPropsChanged == false}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {/if}
    </div>

    {#if showSelectorButtons}
      <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
      <div class="buttons" transition:fade id="table-selector-buttons" tabindex="0" on:keydown={onKeyDown}>
        {#if filterSelectProps.length > 0}
          <Button
            handler={addTableHandler}
            iconOnTheLeft={false}
            disabled={addDataButtonDisabled}
            id="AddData-{uniqueId}"
          >
            <FaPlusSquare slot="icon" />
            <svelte:fragment slot="text">{$t("Show table")}</svelte:fragment>
          </Button>
        {/if}

        <Button handler={backHandler} iconOnTheLeft={false} id="back-{uniqueId}">
          <FaArrowCircleLeft slot="icon" />
          <svelte:fragment slot="text">{$t("Back")}</svelte:fragment>
        </Button>
      </div>
    {/if}
  </div>
  
  <div class="content" id="table-content-{uniqueId}" slot="content" style={showDateRangePicker ? "overflow-y: hidden; height: 100%" : ""}>
    {#if showDateRangePicker}
      <div id="date-range-picker">
        {#if showDatePicker}
          <DatePicker 
          available_dates={tableData.dates}
          value={datePickerValue}
          isDaily={isDaily}
          on:closeDatePicker={() => {showDatePicker = false}}
          on:dateChanged={handleDatePickerSelection}
          />
        {:else}
          <div>
            <p class="date-range-picker-title">{$t("Select date range")}</p>
          </div>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="row date-display-container">
            <div class="col inner">
              <p class="date-display-label">{$t("From")}</p>
              <div class="date-display" on:click={() => {openDatePicker("from")}}>{dateRangePickerFromDate}</div>
            </div>
            <div class="col inner">
              <p class="date-display-label">{$t("To")}</p>
              <div class="date-display" on:click={() => {openDatePicker("to")}}>{dateRangePickerToDate}</div>
            </div>
          </div>
          <div class="row">
            <Button handler={handleDateRangeApply} iconOnTheLeft={false}>
              <svelte:fragment slot="text">{$t("Apply")}</svelte:fragment>
              <svelte:fragment slot="icon"><FaCheckCircle/></svelte:fragment>
            </Button>
            <Button handler={closeDateRangePicker} iconOnTheLeft={false}>
              <svelte:fragment slot="text">{$t("Cancel")}</svelte:fragment>
              <svelte:fragment slot="icon"><FaRegTimesCircle/></svelte:fragment>
            </Button>
          </div>
        {/if}
      </div>
    {/if}
    <Table tableData = {tableData} sortedAscending={sortedAscending} sortedBy={sortedBy}/>
    <p class="table-description" bind:this={description_element}></p>
    <p class="table-source-text" id="table-source-text">{source_text ? $t("Sources") + ": " + source_text : ""}</p>
  </div>
</VisualizationContainer>
{/if}

<!-- Showing an error message when data is unavailable. -->
{#if error_message}
  <CustomError visualizationId={uniqueId} showAdditionalInfo={restoreDataError}>
    <p slot="additional-information" class="additional-error-info">{$t("This error might dissappear on page reload")}</p>
  </CustomError>
{/if}

<style lang="scss">
  .selector {
    display: flex;
    gap: 1rem;
    height: 100%;

    .filters {
      display: flex;
      gap: 0.5rem;
    }

    .buttons {
      display: flex;
      flex-direction: column;
      justify-content: stretch;
      gap: 1rem;

      :global(button:first-child) {
        flex: 1;
        background-color: var(--color-primary);
      }

      :global(button:last-child) {
        flex: 1;
        background-color: var(--color-tertiary);
      }
    }

    :global(.loader-container) {
      background-color: #232124;
    }
  }
  .content {
    text-align: center;
    height: 100%;
    overflow: auto;
    overscroll-behavior: none;
  }

  .date-selector-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.25rem;
    height: 1.25rem;
    color: var(--color-gray);

    &:hover {
      cursor: pointer;
    }

    &.disabled {
      color: var(--color-gray-light);
    }

    .date-range-picker-icon {
    display: flex;
    width: 1.25rem;
    height: 1.25rem;
    color: var(--color-gray);

      .calendar-icon {
        position: absolute;
        height: 1.25rem;
        width: 1.25rem;
      }

      .arrow-icon {
        position: absolute;
        height: 0.65rem;
        width: 0.8rem;
        display: flex;
        background-color: white;
        margin-top: 0.45rem;
        margin-left: 0.24rem;
      }
    }
  }

  #date-range-picker {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 5%;
    left: 50%;
    transform: translateX(-50%);
    justify-content: space-between;
    z-index: 99;
    background-color: white;
    border: 2px solid var(--color-primary);
    border-radius: 0.5rem;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    padding: 1.25rem 1rem;
    width: 350px;
    min-height: 200px;

    .date-range-picker-title {
      font-size: 1rem;
      font-weight: bold;
      margin: 0;
    }

    .date-display-label {
      font-size: 1rem;
      font-weight: bold;
      margin: 0;
      text-align: start;
    }

    .date-display {
      min-width: 70px;
      padding: 0.5rem;
      text-align: center;
      border-radius: 0.25rem;
      background-color: rgb(230, 230, 230);
      font-size: large;
      user-select: none;

      &:hover {
        cursor: pointer;
      }
    }

    .row {
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: 1rem;

      &.date-display-container {
        gap: 3rem;
      }
    }

    .col {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      &.inner {
        justify-content: flex-start;
        gap: 0.5rem;
      }
    }
  }

  .table-description {
    position: sticky;
    left: 0;
  }

  .table-source-text {
    position: sticky;
    left: 0;
  }

  .additional-error-info {
    margin: 0 0 30px 0;
  }
</style>
